import { Box, Drawer } from '@mui/material';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router';

import { useAuth } from '@/lib/hooks/useAuth';
import useDrawerResize from '@/lib/hooks/useDrawerResize';
import { TermsPage } from '@/pages/Terms';
import { coreSelectors } from '@/state/core';

import { Nav } from './Nav';
import { DrawerNav } from './Nav/DrawerNav';
import { PageLoader } from './PageLoader';

export interface PrivateRouteContext {
    setNavComponent?: (component: React.ReactNode | undefined) => void;
}

export const PrivateRoute = () => {
    const { authed, authenticating, termsAccepted } = useAuth();
    const drawerWidth = useSelector(coreSelectors.getDrawerWidth);
    const [menuOpen, setMenuOpen] = useState(true);
    const [tanstackOpen, setTanstackOpen] = useState(false);
    const [panelHeight, setPanelHeight] = useState(500);
    const isResizingPanel = useRef(false);
    const preAuth = !authed && !authenticating;
    const { enableResize } = useDrawerResize({ minWidth: 240 });
    const [navComponent, setNavComponent] = useState<React.ReactNode | undefined>();

    const handleToggleMenu = useCallback(() => {
        setMenuOpen(!menuOpen);
    }, [menuOpen]);

    const handlePanelResizeStart = useCallback(() => {
        isResizingPanel.current = true;
    }, []);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!isResizingPanel.current) return;
            const newHeight = window.innerHeight - e.clientY;
            if (newHeight >= 100) {
                setPanelHeight(newHeight);
            }
        };
        const onMouseUp = () => {
            isResizingPanel.current = false;
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    if (authenticating) {
        return <PageLoader top={0} />;
    }

    if (preAuth) {
        return <Navigate to="/login" replace />;
    }

    if (!termsAccepted) {
        return (
            <>
                <Nav />
                <TermsPage />
            </>
        );
    }

    return (
        <>
            <Nav
                menuOpen={menuOpen}
                onMenuClick={handleToggleMenu}
                tanstackOpen={tanstackOpen}
                onTanstackToggle={() => setTanstackOpen((o) => !o)}
            />
            <Drawer
                variant="persistent"
                open={menuOpen}
                onClose={() => {}}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: drawerWidth,
                        marginTop: '66px',
                    },
                }}
            >
                {navComponent ? navComponent : <DrawerNav />}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '2px',
                        top: '0',
                        right: '-1px',
                        bottom: '0',
                        cursor: 'col-resize',
                    }}
                    onMouseDown={enableResize}
                />
            </Drawer>
            <Box
                sx={{
                    marginLeft: menuOpen ? `${drawerWidth}px` : '',
                    transition: 'margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
                }}
            >
                <Suspense fallback={<PageLoader />}>
                    <Outlet context={{ setNavComponent }} />
                </Suspense>
            </Box>
            {tanstackOpen && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, height: panelHeight }}>
                    <Box
                        sx={{ height: '4px', cursor: 'row-resize', background: 'transparent' }}
                        onMouseDown={handlePanelResizeStart}
                    />
                    <ReactQueryDevtoolsPanel onClose={() => setTanstackOpen(false)} style={{ height: '100%' }} />
                </Box>
            )}
        </>
    );
};
